import { createFileRoute } from '@tanstack/react-router'
import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'
import { SendHorizonal, Layers, Repeat, HelpCircle, Zap, Loader2 } from 'lucide-react'

import { PageContainer } from '@/components/common/page-container'
import { PageHeader } from '@/components/common/page-header'
import { KPICard } from '@/features/dashboard/kpi-card'
import { WeeklyChart } from '@/features/dashboard/weekly-chart'
import { ActivityFeed } from '@/features/dashboard/activity-feed'

import { meQuery } from '@/api/auth'
import { matchesListQuery, type MatchPage } from '@/api/matches'
import { loopsListQuery } from '@/api/loops'
import { api } from '@/api/client'
import { RouteError } from '@/components/common/route-error'

/**
 * /dashboard — KPI cards + 7-day chart + activity feed.
 *
 * KPI strategy (post P0.3): KPIs are now computed from cheap aggregates
 * rather than by paginating /api/matches end-to-end (which used to cost
 * up to 10 requests for a 1000-match account).
 *
 * KPIs are read from the SAME live `/api/matches` surface the Matches page
 * uses, NOT from LoopCV's per-loop `stats` rollup. The rollup is loop-scoped
 * and lags, and it excludes orphan matches from deleted loops — which made
 * the dashboard and the Matches page disagree (dashboard 0, matches 9). Since
 * `/api/matches` is now scoped to active loops, every count below is a cheap
 * `perPage=1` query against it, so the two views can never drift.
 *
 *   - Active loops: from /api/loops items (filter out paused).
 *   - Matches / Needs answers: `total` of a 1-row `/api/matches`
 *     query (optionally filtered), served from active-loop matches only.
 *     Needs answers counts both LoopCV's FORM_HAS_QUESTIONS matches and the
 *     screening forms the Apply Tool parked locally (the backend unions the
 *     two into the same filter).
 *   - Applied: `applied` from /api/matches/apply-stats, i.e. our own
 *     match_applications ledger. NOT the upstream applied-status filter:
 *     extension applies never reach LoopCV, so that count stayed 0 while
 *     the activity feed listed them.
 *
 * The Auto-applyable KPI was dropped: applyPath is a frontend-derived
 * field with no upstream stat equivalent, and we have no way to count
 * it server-side without paginating every match.
 *
 * WeeklyChart + ActivityFeed still need actual match rows. They take
 * the first 100 matches by updatedAt-desc (whatever the list endpoint
 * naturally returns on page 1). Limitation: an account with >100
 * matches in the past 7 days will undercount on the chart; deemed
 * acceptable because the chart is a vibe-check, not a billing report.
 */

const DASHBOARD_PAGE_SIZE = 100

// Live KPI count: returns just the `total` (perPage=1 keeps the body tiny).
// Cache-keyed per metric so the matches table cache isn't polluted.
const matchCountQuery = (metric: string, filter?: string) =>
  queryOptions({
    queryKey: ['matches', 'count', metric] as const,
    queryFn: () =>
      api.get<MatchPage>(`/matches?perPage=1&page=1${filter ? `&filter=${filter}` : ''}`),
    staleTime: 60 * 1000,
  })

// Cheap list-level count of ready matches that advertise dameron, plus the
// applied total from our local ledger. The actual Apply/BatchApply actions
// still detail-validate before firing LoopCV.
const applyStatsQuery = () =>
  queryOptions({
    queryKey: ['matches', 'applyStats'] as const,
    queryFn: () => api.get<{ autoAppliable: number; applied: number }>('/matches/apply-stats'),
    staleTime: 60 * 1000,
  })

export const Route = createFileRoute('/_authed/dashboard')({
  loader: async ({ context }) => {
    // Prefetch in parallel so the KPI grid renders synchronously.
    await Promise.all([
      context.queryClient.ensureQueryData(matchesListQuery(1, DASHBOARD_PAGE_SIZE)),
      context.queryClient.ensureQueryData(loopsListQuery(1, 50)),
      context.queryClient.ensureQueryData(matchCountQuery('total')),
      context.queryClient.ensureQueryData(matchCountQuery('needsAnswers', 'FORM_HAS_QUESTIONS')),
      context.queryClient.ensureQueryData(applyStatsQuery()),
    ])
  },
  component: DashboardPage,
  pendingComponent: DashboardPending,
  errorComponent: RouteError,
})

function DashboardPending() {
  return (
    <PageContainer>
      <div className="flex min-h-[320px] items-center justify-center">
        <div className="text-muted flex items-center gap-2 text-sm font-medium">
          <Loader2 className="size-4 animate-spin" />
          Loading dashboard...
        </div>
      </div>
    </PageContainer>
  )
}

function DashboardPage() {
  const { data: me } = useSuspenseQuery(meQuery())
  const { data: loops } = useSuspenseQuery(loopsListQuery(1, 50))
  const { data: firstPage } = useSuspenseQuery(matchesListQuery(1, DASHBOARD_PAGE_SIZE))
  const { data: totalPage } = useSuspenseQuery(matchCountQuery('total'))
  const { data: needsPage } = useSuspenseQuery(
    matchCountQuery('needsAnswers', 'FORM_HAS_QUESTIONS'),
  )
  const { data: applyStats } = useSuspenseQuery(applyStatsQuery())

  const activeLoops = loops.items.filter((l) => !l.paused).length
  const totalMatches = totalPage.total
  const needsAnswers = needsPage.total
  const applied = applyStats.applied
  const autoAppliable = applyStats.autoAppliable

  return (
    <PageContainer>
      <PageHeader
        title={`Welcome back, ${me.name?.split(' ')[0] ?? me.email}`}
        description="Your loops, matches, and applications at a glance."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <KPICard
          label="Active loops"
          value={activeLoops}
          delta={`${loops.items.length - activeLoops} paused`}
          icon={Repeat}
          variant="primary"
        />
        <KPICard
          label="Matches"
          value={totalMatches}
          delta="all-time"
          icon={Layers}
          variant="info"
        />
        <KPICard
          label="Auto-apply ready"
          value={autoAppliable}
          delta={autoAppliable === 0 ? 'none auto-deliverable now' : 'dameron can apply these'}
          icon={Zap}
          variant="info"
        />
        <KPICard
          label="Applied"
          value={applied}
          delta={
            applied === 0 ? 'No applies yet' : `${Math.max(0, totalMatches - applied)} pending`
          }
          icon={SendHorizonal}
          variant="success"
        />
        <KPICard
          label="Needs answers"
          value={needsAnswers}
          delta={needsAnswers === 0 ? 'no screening Qs waiting' : 'answer to unblock apply'}
          icon={HelpCircle}
          variant="warning"
          to={needsAnswers > 0 ? '/matches' : undefined}
          search={needsAnswers > 0 ? { filter: 'needs_answers' } : undefined}
        />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        {/*
          Chart/feed work on the first 100 matches by default sort
          (updatedAt desc per the matches handler). Good enough for a
          7-day vibe-check; see file-level comment for the limitation.
        */}
        <WeeklyChart matches={firstPage.items} />
        <ActivityFeed matches={firstPage.items} />
      </div>
    </PageContainer>
  )
}
